import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiClient, getApiBase } from '../lib/api';
import { formatMxn } from '../lib/superApi';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = getApiBase();

export default function AdminSubscriptionReturn() {
  const [sp] = useSearchParams();
  const ref = sp.get('ref');
  const mpStatus = sp.get('mp_status');
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState('polling');
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!ref) {
      setStatus('error');
      return undefined;
    }
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await apiClient.get(`${API}/admin/subscription/checkout/status/${ref}`);
        if (cancelled) return;
        setInfo(data);
        if (data.status === 'paid') {
          setStatus('paid');
          await checkAuth?.();
          return;
        }
        if (mpStatus === 'failure' && attempts >= 3) {
          setStatus('error');
          return;
        }
        if (attempts >= 15) {
          setStatus('timeout');
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (cancelled) return;
        if (attempts >= 12) setStatus('error');
        else setTimeout(poll, 2000);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [ref, mpStatus, checkAuth]);

  return (
    <section className="min-h-screen flex items-center justify-center bg-slate-100 px-4" data-testid="admin-subscription-return">
      <div className="bg-white border border-slate-200 p-10 rounded-lg text-center max-w-md w-full shadow-sm">
        {status === 'polling' && (
          <>
            <Loader2 className="mx-auto animate-spin text-teal-600" size={46} />
            <h1 className="text-2xl font-semibold text-slate-900 mt-4">Verificando pago…</h1>
            <p className="text-slate-600 mt-2 text-sm">Confirmando tu suscripción con Mercado Pago.</p>
          </>
        )}
        {status === 'paid' && (
          <>
            <CheckCircle2 className="mx-auto text-green-600" size={64} />
            <h1 className="text-3xl font-semibold text-slate-900 mt-4">¡Suscripción renovada!</h1>
            <p className="text-slate-600 mt-3 text-sm">
              Pago de{' '}
              <span className="text-teal-700 font-bold">{info?.amount ? formatMxn(info.amount) : ''}</span>{' '}
              confirmado.
            </p>
            <Button asChild className="mt-8">
              <Link to="/admin">Ir al panel</Link>
            </Button>
          </>
        )}
        {(status === 'error' || status === 'timeout') && (
          <>
            <XCircle className="mx-auto text-rose-600" size={64} />
            <h1 className="text-2xl font-semibold text-slate-900 mt-4">Pago no confirmado</h1>
            <p className="text-slate-600 mt-2 text-sm">
              {status === 'timeout'
                ? 'Aún no recibimos confirmación. Si ya pagaste, espera unos minutos e intenta de nuevo.'
                : 'El pago no se completó o fue rechazado.'}
            </p>
            <Button asChild variant="outline" className="mt-8">
              <Link to="/admin">Volver al panel</Link>
            </Button>
          </>
        )}
      </div>
    </section>
  );
}
